import axios from "axios";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";

/**
 * Funding Statements — LFS / TFS lifecycle (Projection → Locked → Acknowledged).
 *
 * One generated statement per funding channel. Backend picks the kind (LFS |
 * TFS) automatically from the loan's `fundingType`; this hook just reads the
 * current statement, renders the PDFs, and drives the closer lock + counterparty
 * acknowledgment. The trade/{tradeId} (LPS) variants are intentionally NOT
 * wired here — those belong on the acquisitions wire queue.
 *
 * Mirrors useLoanTermsHook: every method takes
 * `{ dealId, onSuccessFn, onCompleteFn, onFailFn }` (+ extras) and wraps
 * useAxiosHook. PDF views go through axiosGetRequest with `isFile: true,
 * openInNewTab: true`, which streams the blob and opens it in a new tab.
 */
const ROUTE_PREFIX = "funding_statements";

export const useFundingStatementsHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // GET current statement (locked snapshot if any, else a fresh projection).
  const getFundingStatement = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Funding Statement",
      path: endpoint,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // GET the Net Wire Calculator (wire build-down + HUD sources/uses balance).
  const getNetWire = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/net_wire`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Net Wire Calculator",
      path: endpoint,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // GET the settlement documents (HUD-1 / ALTA / Closing Disclosure /
  // preliminary) the Net Wire Calculator can balance against, which one it
  // uses now, and the doc types an upload should carry
  // ({ documents, source, warnings, uploadDocTypes }). Pre-close only.
  const getHudSources = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/hud_sources`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get HUD Sources",
      path: endpoint,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // Choose the HUD the calculator balances against. `payload` is one of:
  //   { documentId }                    — that document
  //   { documentId: null }              — back to automatic
  //   { acknowledgeNewerVersions: true } — dismiss the "newer version" flag
  //                                        (the only one allowed when locked)
  // Returns { netWireCalculator, extractionQueued }. 409 once locked.
  const setHudSource = async ({
    dealId,
    payload,
    onSuccessMsg,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/hud_source`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Set HUD Source",
      path: endpoint,
      payload,
      cancelSource: source,
      onSuccessMsg,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // Open the inline statement PDF (renders the projection on the fly pre-lock)
  // in a new tab.
  const viewFundingStatementPdf = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/pdf`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "View Funding Statement PDF",
      path: endpoint,
      cancelSource: source,
      isFile: true,
      openInNewTab: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // Open the inline Net Wire Calculator PDF in a new tab.
  const viewNetWirePdf = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/net_wire/pdf`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "View Net Wire Calculator PDF",
      path: endpoint,
      cancelSource: source,
      isFile: true,
      openInNewTab: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // CLOSER action — snapshot + render + file PDF into loanDocuments. Idempotent.
  const lockFundingStatement = async ({
    dealId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/lock`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Lock Funding Statement",
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessMsg: "Funding statement locked",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // Counterparty acknowledgment. method is "signed_pdf" (countersigned PDF).
  // Returns 409 when nothing is locked.
  const acknowledgeFundingStatement = async ({
    dealId,
    name,
    method = "signed_pdf",
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ dealId, name });
    const endpoint = `${ROUTE_PREFIX}/${dealId}/acknowledge`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Acknowledge Funding Statement",
      path: endpoint,
      payload: { name, method },
      cancelSource: source,
      onSuccessMsg: "Funding statement acknowledged",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    error,
    loading,
    getFundingStatement,
    getNetWire,
    getHudSources,
    setHudSource,
    viewFundingStatementPdf,
    viewNetWirePdf,
    lockFundingStatement,
    acknowledgeFundingStatement,
  };
};
